import { Directive, ElementRef, HostListener, Input, OnInit, Renderer2 } from "@angular/core";
import { NgControl } from "@angular/forms";

/**
 * Directive to mask input fields.
 * @example
 * <input appMask="999.999.999-99" />
 */
@Directive({
  selector: "[appMask]",
  standalone: true,
})
export class MaskDirective implements OnInit {
  @Input("appMask") mask!: string;
  @Input("allowPartial") allowPartial: boolean = false;
  private lastValue: string = "";

  constructor(
    private el: ElementRef,
    private control: NgControl,
    private renderer: Renderer2,
  ) {}

  ngOnInit() {
    // Garante que o valor inicial está devidamente formatado
    setTimeout(() => {
      const inputEl = this.el.nativeElement;
      if (inputEl.value) {
        this.applyMask(inputEl.value);
      }
    });
  }

  @HostListener("input", ["$event"])
  onInput(event: Event) {
    const inputEl = event.target as HTMLInputElement;
    // Bloqueia caracteres não numéricos
    const value = inputEl.value.replace(/\D/g, "");
    this.applyMask(value);
  }

  @HostListener("keypress", ["$event"])
  onKeyPress(event: KeyboardEvent) {
    // Previne a digitação de caracteres não numéricos
    if (!/\d/.test(event.key)) {
      event.preventDefault();
    }
  }

  private applyMask(value: string) {
    if (!this.mask || !value) return;

    const inputEl = this.el.nativeElement;
    const cursorPosition = inputEl.selectionStart || 0;
    const previousLength = inputEl.value.length;

    // Extrair apenas os dígitos do valor
    const digits = value.replace(/\D/g, "");

    // Se allowPartial é true e a máscara é de cartão (4 grupos de 4 dígitos),
    // aplica formatação flexível para 13-16 dígitos
    if (this.allowPartial && this.mask === "9999 9999 9999 9999") {
      let result = "";
      for (let i = 0; i < digits.length && i < 16; i++) {
        if (i > 0 && i % 4 === 0) {
          result += " ";
        }
        result += digits[i];
      }

      // Evitar que o valor mude se não houver alteração significativa
      if (digits === this.lastValue.replace(/\D/g, "")) {
        return;
      }

      this.lastValue = result;

      // Atualizar o valor do input
      this.renderer.setProperty(inputEl, "value", result);

      // Calcular a nova posição do cursor
      const newPosition = cursorPosition + (result.length - previousLength);
      inputEl.setSelectionRange(newPosition, newPosition);

      // Atualizar o valor do FormControl
      if (this.control.control) {
        this.control.control.setValue(result, { emitEvent: false });
        this.control.control.updateValueAndValidity({ emitEvent: false });
      }
      return;
    }

    // Comportamento padrão da máscara
    // Aplicar a máscara
    let result = "";
    let digitIndex = 0;

    // Percorrer a máscara e aplicar os dígitos
    for (let i = 0; i < this.mask.length && digitIndex < digits.length; i++) {
      if (this.mask[i] === "9") {
        // Posição para dígito
        result += digits[digitIndex++];
      } else {
        // Caracter da máscara
        result += this.mask[i];
      }
    }

    // Evitar que o valor mude se não houver alteração significativa
    // Isso previne o preenchimento automático completo
    if (digits === this.lastValue.replace(/\D/g, "")) {
      return;
    }

    this.lastValue = result;

    // Atualizar o valor do input
    this.renderer.setProperty(inputEl, "value", result);

    // Calcular a nova posição do cursor
    const newPosition = cursorPosition + (result.length - previousLength);
    inputEl.setSelectionRange(newPosition, newPosition);

    // Atualizar o valor do FormControl
    if (this.control.control) {
      this.control.control.setValue(result, { emitEvent: false });
      this.control.control.updateValueAndValidity({ emitEvent: false });
    }
  }
}
